import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'
import { logger } from '@/lib/logger'

/**
 * ErrorBoundary Props
 */
interface ErrorBoundaryProps {
  /** Child components to wrap */
  children: ReactNode
  /** Optional fallback component */
  fallback?: ReactNode
  /** Callback when error is caught */
  onError?: (error: Error, errorInfo: ErrorInfo) => void
}

/**
 * ErrorBoundary State
 */
interface ErrorBoundaryState {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Check if error is a chunk loading error (happens when app is updated during user session)
 * These errors occur when the user has an old HTML file that references chunks
 * that no longer exist after a deployment.
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase()
  return (
    message.includes('failed to fetch dynamically imported module') ||
    message.includes('loading chunk') ||
    message.includes('loading css chunk') ||
    error.name === 'ChunkLoadError'
  )
}

/**
 * ErrorBoundary - Catches React errors and displays a fallback UI
 *
 * Prevents blank white screen crashes by catching errors in child components.
 *
 * @example
 * ```tsx
 * <ErrorBoundary>
 *   <ChatInterface />
 * </ErrorBoundary>
 * ```
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    }
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({ errorInfo })

    // Call optional error callback (for logging to Sentry, etc.)
    this.props.onError?.(error, errorInfo)

    // Log error (includes Sentry reporting in production)
    logger.error('ErrorBoundary caught error', error, {
      componentStack: errorInfo.componentStack
    })

    // Auto-reload for chunk loading errors (deployment cache mismatch)
    // This happens when user has old HTML referencing chunks that no longer exist
    if (isChunkLoadError(error)) {
      logger.info('Chunk load error detected, auto-reloading for fresh assets')
      // Small delay to allow logging to complete
      setTimeout(() => {
        globalThis.location.reload()
      }, 100)
    }
  }

  handleReload = (): void => {
    globalThis.location.reload()
  }

  handleGoHome = (): void => {
    globalThis.location.href = '/'
  }

  handleRetry = (): void => {
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render(): ReactNode {
    if (this.state.hasError) {
      // Custom fallback provided
      if (this.props.fallback) {
        return this.props.fallback
      }

      // Default fallback UI
      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sand-100 via-cream to-sand-100 dark:from-[#1A1A1A] dark:via-[#242424] dark:to-[#1A1A1A] p-4">
          <div className="max-w-md w-full bg-white dark:bg-[#2D2D2D] rounded-2xl shadow-xl p-8 text-center">
            {/* Error icon */}
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-rose-100 dark:bg-rose-900/30 mb-6">
              <AlertTriangle className="w-8 h-8 text-rose-600 dark:text-rose-400" />
            </div>

            {/* Error message */}
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              An unexpected error occurred. Please try again or refresh the page.
            </p>

            {/* Error details (development only) */}
            {import.meta.env.DEV && this.state.error && (
              <div className="mb-6 p-3 bg-gray-100 dark:bg-[#1A1A1A] rounded-lg text-left overflow-auto max-h-32">
                <p className="text-xs font-mono text-red-600 dark:text-red-400">
                  {this.state.error.message}
                </p>
              </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white rounded-lg transition-colors"
              >
                <Home className="w-4 h-4" />
                Go Home
              </button>
            </div>

            {/* Reload hint */}
            <p className="mt-4 text-xs text-gray-500 dark:text-gray-500">
              If the problem persists,{' '}
              <button
                onClick={this.handleReload}
                className="text-blue-600 dark:text-blue-400 hover:underline"
              >
                reload the page
              </button>
            </p>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}
